import { MasonryEngineNodeAction } from '#types/masonry-engine-node.types.ts';

import {
  InvokedAction,
  MasonryActionResult,
} from '#types/masonry-engine-action.types.ts';

type ResolveNextActionOptions = {
  action: InvokedAction<MasonryEngineNodeAction>;
} & (
  | {
      invocationOutcome: 'result';
      result: MasonryActionResult<unknown>;
    }
  | {
      invocationOutcome: 'exception';
      exception: unknown;
    }
);

/**
 * Returns a "next action" object to invoke next based on the result of
 * the primary action, if one is available.
 */
export const resolveNextAction = ({
  action: invokedAction,
  ...rest
}: ResolveNextActionOptions):
  | InvokedAction<MasonryEngineNodeAction>
  | undefined => {
  let nextAction: MasonryEngineNodeAction | undefined;

  if (rest.invocationOutcome === 'result') {
    const { result } = rest;
    nextAction = result.isSuccess
      ? invokedAction.onSuccess
      : invokedAction.onFailure;
  } else {
    const { exception: error } = rest;
    // There's no result. That means the action handler threw an error or rejected.
    // Use the configured onException action, or create a default one if not configured
    nextAction = invokedAction.onException || {
      type: 'handleActionException',
      namespace: 'default',
      payload: {
        error: error instanceof Error ? error.message : String(error),
      },
    };
  }

  return nextAction
    ? {
        ...nextAction,
        // Inherit ancestry from the primary action
        ancestry: invokedAction.ancestry,
        // Preserve event context if it exists
        event: invokedAction.event,
        currentTarget: invokedAction.currentTarget,
        // Force opaque engine-owned origin context from the current action
        origin: invokedAction.origin,
      }
    : // Return nothing if no next action exists
      undefined;
};
